import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, theme, Dropdown, Space, Avatar, Modal, Form, Input, message } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  TransactionOutlined,
  BankOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  SettingOutlined,
  SwapOutlined,
  GiftOutlined,
  DollarOutlined,
  FileTextOutlined,
  TeamOutlined,
  PercentageOutlined,
  WalletOutlined,
  MoneyCollectOutlined,
  HistoryOutlined,
  LockOutlined
} from '@ant-design/icons';
import { AdminUser } from '../services/api';
import { updatePassword } from '../services/admin';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  useEffect(() => {
    const userStr = localStorage.getItem('admin_user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  const getMenuItems = (): MenuProps['items'] => {
    const baseItems: MenuProps['items'] = [
      {
        key: '/users',
        icon: <UserOutlined />,
        label: '用户管理',
      },
      {
        key: '/referral',
        icon: <TeamOutlined />,
        label: '推广管理',
      },
      {
        key: '/earnings',
        icon: <MoneyCollectOutlined />,
        label: '用户收益管理',
      },
      {
        key: '/earnings-history',
        icon: <HistoryOutlined />,
        label: '历史收益记录',
      },
      {
        key: '/withdrawals',
        icon: <WalletOutlined />,
        label: '提现管理',
      }
    ];

    // 只有超级管理员可以看到其他功能
    if (user?.level_id === 1) {
      baseItems.push(
        {
          key: '/lending',
          icon: <SwapOutlined />,
          label: '借贷管理',
        },
        {
          key: '/loan-draw',
          icon: <MoneyCollectOutlined />,
          label: '提款审核管理',
        },
        {
          key: '/profit-rate',
          icon: <PercentageOutlined />,
          label: '收益比率',
        },
        {
          key: '/content',
          icon: <FileTextOutlined />,
          label: '内容管理',
          children: [
            {
              key: '/content/announcements',
              icon: <FileTextOutlined />,
              label: '公告管理',
            },
          ],
        },
        {
          key: '/admin',
          icon: <TeamOutlined />,
          label: '管理员',
        }
      );
    }

    return baseItems;
  };

  const userMenuItems = [
    {
      key: 'password',
      icon: <LockOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = (item: { key: string }) => {
    console.log('Menu item clicked:', item.key);
    // 确保在导航前检查认证状态
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');
    
    if (!token || !userData) {
      console.log('No auth data found, redirecting to login');
      navigate('/login');
      return;
    }
    
    navigate(item.key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/login');
    } else if (key === 'password') {
      setPasswordModalVisible(true);
    }
  };

  const handlePasswordChange = async (values: { oldPassword: string; newPassword: string }) => {
    try {
      const response = await updatePassword(values);
      if (response && response.code === 0) {
        message.success('密码修改成功');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(response?.message || '密码修改失败');
      }
    } catch (error) {
      message.error('密码修改失败');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed}>
        <div className="logo" />
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={getMenuItems()}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: 0, background: colorBgContainer, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <div className="header-right">
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
              <Space style={{ cursor: 'pointer', padding: '0 24px' }}>
                <Avatar icon={<UserOutlined />} />
                <span>{user?.username}</span>
                <span style={{ color: '#999', fontSize: '12px' }}>
                  ({user?.level_id === 1 ? '超级管理员' : '普通管理员'})
                </span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            background: colorBgContainer,
            minHeight: 280,
          }}
        >
          {children}
        </Content>
      </Layout>

      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          onFinish={handlePasswordChange}
          layout="vertical"
        >
          <Form.Item
            name="oldPassword"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度不能少于6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MainLayout;